import { Component, OnInit } from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import Swal from "sweetalert2";
import { DataService } from "../../../services/data.service";
import { MatSelectChange } from "@angular/material/select";

interface MenuItem {
  _id: string;
  label: string;
  url: string;
  icon: string;
  visible_for: string[];
  checked: boolean;
  submenus: MenuItem[];
}

export type newMenuItem = {
  _id: string;
  created_at: Date;
  updated_at: Date;
  category: string;
  menus: MenuItem[];
};

@Component({
  selector: "app-configs",
  templateUrl: "./configs.component.html",
  styleUrls: ["./configs.component.css"],
})
export class ConfigsComponent implements OnInit {
  constructor(private dataservice: DataService) {}
  userMenuData: Record<string, string[]> = {};
  availableMenuData: newMenuItem[] = [];
  enabledMenus: string[] = [];
  enabledSubMenus: string[] = [];
  configUpdatesData = new Map();
  menuSubmenuCounts: Record<string, number> = {};
  userTypes: {
    _id: string;
    name: string;
  }[] = [];
  selectedUserType: string = null;
  isUserMenuFetched: boolean = false;
  availableMenus: {
    category: string;
    menus: string[];
  }[] = [];
  selectedCategory: string = null;
  selectedMenu: string = null;
  selectedSubMenu: string = null;
  menusForSelectedCategory: string[] = [];
  subMenusForSelectedCategory: string[] = [];

  ngOnInit(): void {
    this.getUserTypes();
  }

  private getUserTypes() {
    this.dataservice.getNewSidebarUserTypes().subscribe({
      next: (res) => {
        if (res.body.success) {
          this.userTypes = res.body.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  private getUserMenuCofigs = () => {
    this.dataservice.getNewMenuUserAccess(this.selectedUserType).subscribe({
      next: (res) => {
        if (res.body.success) {
          this.userMenuData = res.body.data;
          const defaultMenuData = res.body.new_menu_defaults;

          Object.keys(this.userMenuData).forEach((menu: string) => {
            const submenus = this.userMenuData[menu] as string[];

            this.enabledMenus.push(menu);
            this.enabledSubMenus.push(...submenus);
          });

          // get all menu related data and check on items based on user's data
          this.dataservice.getNewSidebarMenuConfigs().subscribe({
            next: (response) => {
              this.availableMenus = response.body.categories;

              // set checked property to false on each item initially and validate against the user data
              this.availableMenuData = response.body.data.map((_) => {
                if (_._id === defaultMenuData.category) {
                  this.selectedCategory = _.category;
                  this.menusForSelectedCategory = _.menus.map((m) => m.label);
                }

                return {
                  ..._,
                  menus: _.menus.map((m) => {
                    const value = this.enabledMenus.includes(m._id);
                    this.configUpdatesData.set(m._id, value);
                    this.menuSubmenuCounts[m._id] = m.submenus.length;

                    if (
                      _._id === defaultMenuData.category &&
                      m._id === defaultMenuData.menu
                    ) {
                      this.selectedMenu = m.label;
                      this.subMenusForSelectedCategory = m.submenus.map(
                        (_) => _.label,
                      );
                    }

                    return {
                      ...m,
                      checked: value,
                      submenus: m.submenus.map((sm) => {
                        const key = `${m._id}-${sm._id}`;
                        const value = this.enabledSubMenus.includes(sm._id);
                        this.configUpdatesData.set(key, value);

                        if (
                          _._id === defaultMenuData.category &&
                          m._id === defaultMenuData.menu &&
                          sm._id === defaultMenuData.submenu
                        ) {
                          this.selectedSubMenu = sm.label;
                        }

                        return {
                          ...sm,
                          checked: value,
                        };
                      }),
                    };
                  }),
                };
              });
            },
            error(err) {
              console.log(err);
            },
          });
        }
      },
    });
  };

  private getMenuUpdatePayload = () => {
    const payload: Record<string, string[]> = {};

    this.configUpdatesData.forEach((value, key) => {
      const [menuId, submenuId] = key.split("-");
      const hasSubmenus = this.menuSubmenuCounts[menuId] > 0;
      const isChecked = value === true;

      if (isChecked) {
        // if a menu is checked and doesn't have submenu means it is selected too
        if (!hasSubmenus) {
          payload[menuId] = [];
        } else {
          // first one to be selected:
          if (submenuId && !payload[menuId]) {
            payload[menuId] = [submenuId];
          } else if (submenuId) {
            payload[menuId].push(submenuId);
          }
        }
      }
    });

    return payload;
  };

  onSaveConfig() {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Save Config",
      cancelButtonText: "Discard",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        const payload = { ...this.getMenuUpdatePayload() };

        const selectedMenuCategory = this.availableMenuData.find(
          (_) => _.category === this.selectedCategory,
        );

        const selectedMenuItem = selectedMenuCategory?.menus.find(
          (_) => _?.label === this.selectedMenu,
        );

        const categoryId = selectedMenuCategory?._id ?? null;
        const menuId = selectedMenuItem?._id ?? null;
        const submenuId =
          selectedMenuItem.submenus.find(
            (_) => _.label === this.selectedSubMenu,
          )?._id ?? null;

        const new_menu_defaults = {
          category: categoryId,
          menu: menuId,
          submenu: submenuId,
        };

        this.dataservice
          .setNewMenuUserAccess({
            user_id: this.selectedUserType,
            menu_access_list: payload,
            new_menu_defaults: new_menu_defaults,
          })
          .subscribe({
            next: () => {
              this.selectedUserType = null;
              this.resetStates();
            },
            error(err) {
              console.log(err);
            },
          });

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Config saved Successfully",
        });
      }
    });
  }

  onCheckBoxClicked = (event: MatCheckboxChange) => {
    const {
      checked,
      source: { id },
    } = event;
    const [menuId, submenuId] = id.split("-");
    const key = `${menuId}${submenuId ? "-" + submenuId : ""}`;

    this.configUpdatesData.set(key, checked);
  };

  onUserTypeChange(event: MatSelectChange) {
    this.selectedUserType = event.value;
    this.resetStates();
    this.getUserMenuCofigs();
    this.isUserMenuFetched = true;
  }

  onCategoryChange(category: string): void {
    this.selectedCategory = category;
    this.menusForSelectedCategory = this.availableMenus.find(
      (_) => _.category === this.selectedCategory,
    ).menus;
    this.selectedMenu = null;
    this.selectedSubMenu = null;
  }

  onMenuChange(menu: string): void {
    this.selectedMenu = menu;

    this.subMenusForSelectedCategory = this.availableMenuData
      .find((_) => _.category === this.selectedCategory)
      .menus.find((_) => _.label === this.selectedMenu)
      .submenus.map((_) => _.label);
    this.selectedSubMenu = null;
  }

  onSubMenuChange(submenu: string): void {
    this.selectedSubMenu = submenu;
  }

  private resetStates() {
    this.isUserMenuFetched = false;
    this.enabledMenus = [];
    this.enabledSubMenus = [];
    this.configUpdatesData = new Map();
    this.selectedCategory = null;
    this.selectedMenu = null;
    this.selectedSubMenu = null;
  }
}
